import noop from 'lodash/noop';
import React, { useState } from 'react';

import { useCompleteFragment } from '@eluve/apollo-client';
import { Stack, Text, useToast } from '@eluve/components';
import {
  DynamicArtifactTemplate,
  hydrateDynamicArtifactTemplate,
} from '@eluve/llm-outputs';
import {
  DynamicArtifactRenderer,
  SummarySectionMarkdown,
} from '@eluve/smart-blocks';
import { Translator } from '@eluve/translation';

import { useAppointmentParams } from '../useAppointmentParams';

import { draftContentFragment } from './operations';
import { useSummary } from './useSummary';

export const DraftContent: React.FC = () => {
  const { appointmentId } = useAppointmentParams();
  const toast = useToast();

  const appointment = useCompleteFragment({
    fragment: draftContentFragment,
    key: { id: appointmentId },
  });

  const { humanSummary, outputTemplate } = useSummary({
    appointmentId,
  });

  let translatedHydratedHumanOutput: DynamicArtifactTemplate | undefined =
    undefined;
  const humanSoapNote = appointment?.humanOutputs?.[0];
  const humanSoapNoteContent = humanSoapNote?.output?.content;

  const toTranslate = {
    humanSoapNoteContent,
    additionalNotes: appointment?.doctor_interaction?.additionalNotes,
  };

  const [parsedTranslatedContent, setParsedTranslatedContent] = useState<
    typeof toTranslate | null
  >(null);

  const handleTranslated = (translated: string[] | null) => {
    if (!translated || translated.length === 0) {
      setParsedTranslatedContent(null);
      return;
    }
    try {
      setParsedTranslatedContent(JSON.parse(translated[0]!));
    } catch {
      toast.error('Could not apply translation');
      setParsedTranslatedContent(null);
    }
  };

  if (!humanSummary) {
    return (
      <Stack gap={2}>
        <p>No human summary available for this appointment.</p>
      </Stack>
    );
  }

  const translatedHumanOutputContent =
    parsedTranslatedContent?.humanSoapNoteContent;
  const translatedAdditionalNotes = parsedTranslatedContent?.additionalNotes;

  if (parsedTranslatedContent && outputTemplate) {
    translatedHydratedHumanOutput = hydrateDynamicArtifactTemplate(
      outputTemplate,
      translatedHumanOutputContent ?? {},
    );
  }

  return (
    <Stack gap={2}>
      <div className="w-1/6">
        <Text size="md">Need translation?</Text>
        <Translator
          items={[JSON.stringify(toTranslate)]}
          onTranslated={handleTranslated}
        />
      </div>
      <Stack align="start" gap={2} direction="horizontal">
        <Stack gap={2}>
          <Text size="md" inline>
            Doctor Draft
          </Text>

          <DynamicArtifactRenderer
            isReadonly={true}
            blocks={humanSummary.blocks}
          />

          <div>
            {appointment?.doctor_interaction?.additionalNotes && (
              <SummarySectionMarkdown
                key="AdditionalNotes"
                summaryKey={`AdditionalNotes` as any}
                sectionTitle="Additional Notes"
                content={appointment?.doctor_interaction?.additionalNotes}
                disabled={true}
                handleContentChange={noop}
              />
            )}
          </div>
        </Stack>
        {translatedHydratedHumanOutput && (
          <Stack gap={2}>
            <Text size="md">Doctor Draft (translated)</Text>

            <DynamicArtifactRenderer
              isReadonly={true}
              blocks={translatedHydratedHumanOutput.blocks}
            />

            <div>
              {translatedAdditionalNotes && (
                <SummarySectionMarkdown
                  className="w-full"
                  key={'AdditionalNotes' + translatedAdditionalNotes}
                  summaryKey={`AdditionalNotes` as any}
                  sectionTitle="Additional Notes"
                  content={translatedAdditionalNotes}
                  disabled={true}
                  handleContentChange={noop}
                />
              )}
            </div>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
